const EMAIL_ENCRYPTION_ALGORITHM = "AES-GCM";
const EMAIL_IV_LENGTH = 12;
const EMAIL_SHARED_SECRET = process.env.REACT_APP_EMAIL_AES_KEY || "";

const textEncoder = new TextEncoder();

function bytesToBase64(bytes) {
  return btoa(String.fromCharCode(...bytes));
}

async function getEmailEncryptionKey() {
  const normalizedSecret = String(EMAIL_SHARED_SECRET || "").trim();

  if (!normalizedSecret) {
    throw new Error("REACT_APP_EMAIL_AES_KEY is not configured.");
  }

  const secretBytes = textEncoder.encode(normalizedSecret);
  const derivedKeyBuffer = await crypto.subtle.digest("SHA-256", secretBytes);

  return crypto.subtle.importKey(
    "raw",
    derivedKeyBuffer,
    { name: EMAIL_ENCRYPTION_ALGORITHM },
    false,
    ["encrypt", "decrypt"]
  );
}

export async function encryptEmail(email) {
  const normalizedEmail = String(email || "").trim();

  if (!normalizedEmail) {
    throw new Error("Email is required for encryption.");
  }

  const encryptionKey = await getEmailEncryptionKey();
  const iv = crypto.getRandomValues(new Uint8Array(EMAIL_IV_LENGTH));
  const encryptedBuffer = await crypto.subtle.encrypt(
    {
      name: EMAIL_ENCRYPTION_ALGORITHM,
      iv,
    },
    encryptionKey,
    textEncoder.encode(normalizedEmail)
  );

  return {
    email: bytesToBase64(new Uint8Array(encryptedBuffer)),
    emailIv: bytesToBase64(iv),
  };
}

export async function decryptEmail(encryptedEmail, emailIv) {
  const normalizedEncryptedEmail = String(encryptedEmail || "").trim();
  const normalizedEmailIv = String(emailIv || "").trim();

  if (!normalizedEncryptedEmail || !normalizedEmailIv) {
    return encryptedEmail;
  }

  try {
    const encryptionKey = await getEmailEncryptionKey();
    const cipherBytes = Uint8Array.from(atob(normalizedEncryptedEmail), (c) =>
      c.charCodeAt(0)
    );
    const iv = Uint8Array.from(atob(normalizedEmailIv), (c) => c.charCodeAt(0));

    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: EMAIL_ENCRYPTION_ALGORITHM,
        iv,
      },
      encryptionKey,
      cipherBytes
    );

    return new TextDecoder().decode(decryptedBuffer);
  } catch (error) {
    console.warn("Failed to decrypt email response, returning original value.");
    return encryptedEmail;
  }
}
